import { useState, useEffect, useContext, useMemo } from "react";
import SharedDataContext from "../_helper/SharedData";
import { getPayoutAllowedCities } from "../Services/Payout.service";

/**
 * Custom hook that encapsulates the "allowed cities" gate logic used by
 * DriverPayout and DriverDeductionApproval.
 *
 * Fetches the list of payout-enabled city IDs on mount, then derives:
 *  - whether the currently-selected city is allowed
 *  - a human-readable message listing the allowed city names
 *
 * @returns {{
 *   allowedCitiesLoading: boolean,
 *   isCityAllowed: boolean,
 *   allowedCityNamesMsg: string
 * }}
 */
export function usePayoutAllowedCities(customCityId = null) {
    const { globalCityField, allCityData } = useContext(SharedDataContext);
    const serviceCityId = customCityId !== null ? customCityId : (globalCityField?.value?.id || null);

    const [allowedCities, setAllowedCities] = useState([]);
    const [allowedCitiesLoading, setAllowedCitiesLoading] = useState(true);

    useEffect(() => {
        const fetchAllowedCities = async () => {
            try {
                const response = await getPayoutAllowedCities();
                if (response && response.data) {
                    setAllowedCities(response.data);
                }
            } catch (error) {
                console.error("PAYOUT_ALLOWED_CITIES_HOOK_FETCH_FAILED", error);
            } finally {
                setAllowedCitiesLoading(false);
            }
        };
        fetchAllowedCities();
    }, []);

    const isCityAllowed =
        !allowedCitiesLoading && allowedCities.includes(serviceCityId);

    const allowedCityNamesMsg = useMemo(() => {
        if (
            !allCityData ||
            !Array.isArray(allowedCities) ||
            !allowedCities.length
        ) {
            return "these cities";
        }

        const names = allCityData
            .filter((city) => allowedCities.includes(city.id))
            .map((city) => city.cityName || city.name || city.value);

        if (names.length === 0) return "these cities";
        if (names.length === 1) return names[0];
        if (names.length === 2) return `${names[0]} and ${names[1]}`;
        return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
    }, [allCityData, allowedCities]);

    return { allowedCitiesLoading, isCityAllowed, allowedCityNamesMsg };
}
